import { useState } from 'react'
import { Input, Select, Textarea } from '../../components/forms'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'
import { formatCurrency, formatDateInput } from '../../lib/format'

function InvoiceForm({
  initialData,
  businesses = [],
  clients = [],
  onSubmit,
  onCancel,
  loading = false,
}) {
  // Default due date to 30 days from now
  const defaultDueDate = new Date()
  defaultDueDate.setDate(defaultDueDate.getDate() + 30)

  const [formData, setFormData] = useState({
    business_id: initialData?.business_id || '',
    client_id: initialData?.client_id || '',
    due_date: formatDateInput(initialData?.due_date || defaultDueDate),
    tax_rate: initialData?.tax_rate || 0,
    notes: initialData?.notes || '',
    terms: initialData?.terms || 'Payment is due within 30 days of invoice date.',
  })

  const [items, setItems] = useState(
    initialData?.invoice_items?.length
      ? initialData.invoice_items.map(item => ({
          id: item.id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          amount: item.amount,
        }))
      : [{ id: null, description: '', quantity: 1, unit_price: 0, amount: 0 }]
  )

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleItemChange = (index, field, value) => {
    setItems(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }

      // Recalculate amount
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(updated[index].quantity) || 0
        const price = field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(updated[index].unit_price) || 0
        updated[index].amount = qty * price
      }

      return updated
    })
  }

  const addItem = () => {
    setItems(prev => [...prev, { id: null, description: '', quantity: 1, unit_price: 0, amount: 0 }])
  }

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(prev => prev.filter((_, i) => i !== index))
    }
  }

  const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
  const taxAmount = subtotal * ((parseFloat(formData.tax_rate) || 0) / 100)
  const total = subtotal + taxAmount

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit({
      ...formData,
      items: items.filter(item => item.description.trim()),
      subtotal,
      tax_amount: taxAmount,
      total,
    })
  }

  const businessOptions = businesses.map(b => ({
    value: b.id,
    label: `${b.emoji} ${b.name}`,
  }))

  // Filter clients by selected business
  const filteredClients = formData.business_id
    ? clients.filter(c => c.business_id === formData.business_id)
    : clients

  const clientOptions = filteredClients.map(c => ({
    value: c.id,
    label: c.company ? `${c.name} (${c.company})` : c.name,
  }))

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-2 mb-4">
        <Select
          label="Business"
          name="business_id"
          value={formData.business_id}
          onChange={handleChange}
          options={businessOptions}
          placeholder="Select business..."
          required
        />
        <Select
          label="Client"
          name="client_id"
          value={formData.client_id}
          onChange={handleChange}
          options={clientOptions}
          placeholder="Select client..."
          required
          disabled={!formData.business_id}
        />
      </div>

      <div className="grid grid-2 mb-4">
        <Input
          label="Due Date"
          name="due_date"
          type="date"
          value={formData.due_date}
          onChange={handleChange}
          required
        />
        <Input
          label="Tax Rate (%)"
          name="tax_rate"
          type="number"
          step="0.01"
          min="0"
          max="100"
          value={formData.tax_rate}
          onChange={handleChange}
        />
      </div>

      {/* Line Items */}
      <Card className="mb-4">
        <h4 className="font-medium mb-3">Line Items</h4>
        <table className="table" style={{ marginBottom: '1rem' }}>
          <thead>
            <tr>
              <th style={{ width: '50%' }}>Description</th>
              <th style={{ width: '15%' }}>Qty</th>
              <th style={{ width: '15%' }}>Price</th>
              <th style={{ width: '15%' }}>Amount</th>
              <th style={{ width: '5%' }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index}>
                <td>
                  <input
                    type="text"
                    className="form-input"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    placeholder="Service or product description"
                    required
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className="form-input"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className="form-input"
                    value={item.unit_price}
                    onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </td>
                <td className="font-medium">
                  {formatCurrency(item.amount)}
                </td>
                <td>
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="btn btn-ghost btn-sm"
                      style={{ color: 'var(--error)' }}
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Button type="button" variant="secondary" size="sm" onClick={addItem}>
          + Add Line Item
        </Button>
      </Card>

      {/* Totals */}
      <Card className="mb-4" style={{ background: 'var(--gray-50)' }}>
        <div className="flex justify-between py-1">
          <span>Subtotal:</span>
          <span className="font-medium">{formatCurrency(subtotal)}</span>
        </div>
        {parseFloat(formData.tax_rate) > 0 && (
          <div className="flex justify-between py-1">
            <span>Tax ({formData.tax_rate}%):</span>
            <span className="font-medium">{formatCurrency(taxAmount)}</span>
          </div>
        )}
        <div className="flex justify-between py-2 border-t mt-2" style={{ borderColor: 'var(--gray-200)' }}>
          <span className="font-bold">Total:</span>
          <span className="font-bold text-lg">{formatCurrency(total)}</span>
        </div>
      </Card>

      <Textarea
        label="Notes (visible to client)"
        name="notes"
        value={formData.notes}
        onChange={handleChange}
        placeholder="Thank you for your business!"
        rows={2}
      />

      <Textarea
        label="Payment Terms"
        name="terms"
        value={formData.terms}
        onChange={handleChange}
        rows={2}
      />

      <div className="flex justify-between gap-3 mt-6">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={loading}>
          {initialData ? 'Update' : 'Create'} Invoice
        </Button>
      </div>
    </form>
  )
}

export default InvoiceForm
